import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Avatar,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { tokens } from "../../../../theme";

interface IYouTubeChannel {
  id: string;
  name: string;
  logo: string;
  email?: string;
}

interface ISelectYouTubeChannelModalProps {
  open: boolean;
  onClose: () => void;
  channels: IYouTubeChannel[];
  onSelect: (channel: IYouTubeChannel) => void;
}

const SelectYouTubeChannelModal: React.FC<ISelectYouTubeChannelModalProps> = ({
  open,
  onClose,
  channels,
  onSelect,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
      >
        <DialogTitle>Select YouTube Channel</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Choose which YouTube channel you’d like to stream to.
          </Typography>

          <Stack spacing={2}>
            {channels.map((channel) => (
              <motion.div
                key={channel.id}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={2}
                  py={1.5}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "0.2s",
                    "&:hover": { backgroundColor: colors.primary[800] },
                  }}
                  onClick={() => onSelect(channel)}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar src={channel.logo} alt={channel.name} />
                    <Box>
                      <Typography variant="subtitle1">
                        {channel.name}
                      </Typography>
                      {channel.email && (
                        <Typography variant="caption" color="text.secondary">
                          {channel.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Button size="small" variant="outlined">
                    Select
                  </Button>
                </Box>
              </motion.div>
            ))}
          </Stack>
        </DialogContent>
      </motion.div>
    </Dialog>
  );
};

export default SelectYouTubeChannelModal;
